import React from 'react';
import PropTypes from 'prop-types';
import { Card, Button, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faCheck } from '@fortawesome/free-solid-svg-icons';
import {
  calculateMeasurableWeekTotal,
  calculateSectorSicScore,
  calculateWeeklyAverage,
  compareTypeSymbol,
  formatSicPercent,
  formatTargetDisplay,
  isFormulaMeasurable,
  isMeasurableCountedInSic,
  isSicScorePassing,
  isTargetEditable as canEditTargetManually,
  parseFormulaJson,
  SIC_PASS_THRESHOLD
} from 'helpers/sicHelper';
import SicValueCell from './SicValueCell';
import {
  fixedLayoutTableStyle,
  resultColors,
  SIC_COLUMN_WIDTHS
} from './sicTableStyles';

const isWeekendDate = date => date.day() === 0 || date.day() === 6;

export const buildSicCellKey = (measurableId, dayIndex) =>
  `${measurableId}|${dayIndex}`;

const smallEditIconStyle = {
  marginLeft: '6px',
  fontSize: '0.65rem',
  opacity: 0.5
};

const formulaBadgeStyle = {
  marginLeft: '6px',
  fontSize: '0.65rem',
  fontStyle: 'italic',
  fontWeight: 'bold',
  opacity: 0.6
};

const formulaTooltipText = formulaJson => {
  const parsedFormula = parseFormulaJson(formulaJson);
  return parsedFormula ? `= ${parsedFormula.formulaWithNames}` : undefined;
};

export const SicColumnGroup = ({ dayCount }) => (
  <colgroup>
    <col style={{ width: `${SIC_COLUMN_WIDTHS.measurable}px` }} />
    <col style={{ width: `${SIC_COLUMN_WIDTHS.uom}px` }} />
    <col style={{ width: `${SIC_COLUMN_WIDTHS.compare}px` }} />
    <col style={{ width: `${SIC_COLUMN_WIDTHS.target}px` }} />
    <col style={{ width: `${SIC_COLUMN_WIDTHS.affectsSic}px` }} />
    {Array.from({ length: dayCount }, (_, dayIndex) => (
      <col key={dayIndex} style={{ width: `${SIC_COLUMN_WIDTHS.day}px` }} />
    ))}
    <col style={{ width: `${SIC_COLUMN_WIDTHS.total}px` }} />
  </colgroup>
);

SicColumnGroup.propTypes = {
  dayCount: PropTypes.number.isRequired
};

export default function SicSectorCard({
  sector,
  weekDates,
  styles,
  savingCellKeys,
  onCommitValue,
  onEditSector,
  onEditMeasurable,
  onCreateMeasurable,
  onEditTarget
}) {
  const sectorSicScoresByDay = weekDates.map((_, dayIndex) =>
    calculateSectorSicScore(sector.measurables, dayIndex)
  );
  const sectorWeeklySicScore = calculateWeeklyAverage(sectorSicScoresByDay);

  const headerCellFor = date => ({
    ...styles.headerCell,
    ...(isWeekendDate(date) ? styles.weekendHeaderCell : {})
  });

  const sicScoreColors = score =>
    resultColors(styles, score === null ? null : isSicScorePassing(score));

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <h5 className="mb-0 me-2">{sector.name}</h5>
          <FontAwesomeIcon
            size="xs"
            icon={faPencilAlt}
            style={{ cursor: 'pointer', opacity: 0.7 }}
            onClick={() => onEditSector(sector)}
          />
        </div>
        <Button size="sm" onClick={() => onCreateMeasurable(sector)}>
          Create Measurable
        </Button>
      </Card.Header>
      <Card.Body className="pt-0">
        {!sector.measurables.length ? (
          <div className="text-muted fs--1 py-2">
            No measurables yet. Click &quot;Create Measurable&quot; to add one.
          </div>
        ) : (
          <Table responsive style={fixedLayoutTableStyle}>
            <SicColumnGroup dayCount={weekDates.length} />
            <thead>
              <tr>
                <th style={{ ...styles.headerCell, textAlign: 'left' }}>
                  Measurable
                </th>
                <th style={styles.headerCell}>UOM</th>
                <th style={styles.headerCell}>Compare</th>
                <th style={styles.headerCell}>Target</th>
                <th style={styles.headerCell}>Affects SIC</th>
                {weekDates.map(date => (
                  <th
                    key={date.format('YYYY-MM-DD')}
                    style={headerCellFor(date)}
                  >
                    <div>{date.format('ddd')}</div>
                    <div style={{ fontWeight: 'normal' }}>
                      {date.format('DD-MMM')}
                    </div>
                  </th>
                ))}
                <th style={styles.headerCell}>Total</th>
              </tr>
            </thead>
            <tbody>
              {sector.measurables.map((measurable, rowIndex) => {
                const rowBackground = styles.zebraBackground(rowIndex);
                const plainCell = {
                  ...styles.bodyCell,
                  backgroundColor: rowBackground,
                  textAlign: 'center'
                };
                const weekTotal = calculateMeasurableWeekTotal(measurable);
                const isTargetEditable = canEditTargetManually(measurable);
                return (
                  <tr key={measurable.id}>
                    <td style={{ ...plainCell, textAlign: 'left' }}>
                      <span
                        style={{ cursor: 'pointer' }}
                        onClick={() => onEditMeasurable(measurable)}
                        title={
                          isFormulaMeasurable(measurable)
                            ? formulaTooltipText(measurable.formula)
                            : undefined
                        }
                      >
                        {measurable.name}
                        {isFormulaMeasurable(measurable) && (
                          <span style={formulaBadgeStyle}>fx</span>
                        )}
                        <FontAwesomeIcon
                          icon={faPencilAlt}
                          style={smallEditIconStyle}
                        />
                      </span>
                    </td>
                    <td style={plainCell}>{measurable.displayUom}</td>
                    <td style={plainCell}>
                      {compareTypeSymbol(measurable.compareType)}
                    </td>
                    <td
                      style={{
                        ...plainCell,
                        cursor: isTargetEditable ? 'pointer' : 'default'
                      }}
                      onClick={() =>
                        isTargetEditable && onEditTarget(measurable)
                      }
                      title={formulaTooltipText(measurable.targetFormula)}
                    >
                      {formatTargetDisplay(measurable)}
                      {parseFormulaJson(measurable.targetFormula) && (
                        <span style={formulaBadgeStyle}>fx</span>
                      )}
                      {isTargetEditable && (
                        <FontAwesomeIcon
                          icon={faPencilAlt}
                          style={smallEditIconStyle}
                        />
                      )}
                    </td>
                    <td style={plainCell}>
                      {isMeasurableCountedInSic(measurable) && (
                        <FontAwesomeIcon
                          icon={faCheck}
                          style={{ color: '#00a35c' }}
                        />
                      )}
                    </td>
                    {weekDates.map((date, dayIndex) => (
                      <SicValueCell
                        key={date.format('YYYY-MM-DD')}
                        measurable={measurable}
                        dayIndex={dayIndex}
                        isSaving={savingCellKeys.has(
                          buildSicCellKey(measurable.id, dayIndex)
                        )}
                        onCommit={value =>
                          onCommitValue(measurable, dayIndex, value)
                        }
                        styles={styles}
                        rowBackground={rowBackground}
                      />
                    ))}
                    <td style={{ ...plainCell, fontWeight: 600 }}>
                      {weekTotal ?? '-'}
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td
                  colSpan={4}
                  style={{ ...styles.sicRowCell, textAlign: 'left' }}
                >
                  SIC Score
                </td>
                <td style={styles.sicRowCell}>
                  {`>= ${Math.round(SIC_PASS_THRESHOLD * 100)}%`}
                </td>
                {sectorSicScoresByDay.map((score, dayIndex) => (
                  <OverlayTrigger
                    key={dayIndex}
                    overlay={
                      <Tooltip>
                        Measurables on target / measurables affecting SIC
                      </Tooltip>
                    }
                  >
                    <td
                      style={{ ...styles.sicRowCell, ...sicScoreColors(score) }}
                    >
                      {formatSicPercent(score)}
                    </td>
                  </OverlayTrigger>
                ))}
                <td
                  style={{
                    ...styles.sicRowCell,
                    ...sicScoreColors(sectorWeeklySicScore)
                  }}
                >
                  {formatSicPercent(sectorWeeklySicScore)}
                </td>
              </tr>
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}

SicSectorCard.propTypes = {
  sector: PropTypes.object.isRequired,
  weekDates: PropTypes.array.isRequired,
  styles: PropTypes.object.isRequired,
  savingCellKeys: PropTypes.instanceOf(Set).isRequired,
  onCommitValue: PropTypes.func.isRequired,
  onEditSector: PropTypes.func.isRequired,
  onEditMeasurable: PropTypes.func.isRequired,
  onCreateMeasurable: PropTypes.func.isRequired,
  onEditTarget: PropTypes.func.isRequired
};
